import { Fragment, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MENU_SIDEBAR_MAIN } from '@/config/layout.config';
import { useMenu } from '@/hooks/useMenu';
import {
  buildBreadcrumbTrail,
  resolveModuleHomePath,
  HOME_PATH,
  type PortalType,
} from '@/lib/breadcrumb';
import { useBreadcrumbTrailLeaf } from './breadcrumb-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@marine/ui/breadcrumb';

export interface ToolbarHeadingProps {
  title?: string | ReactNode;
  description?: string | ReactNode;
}

function Toolbar({ children }: { children?: ReactNode }) {
  return (
    <div className="py-2.5 lg:py-0 lg:fixed top-(--header-height) start-[calc(var(--sidebar-width))] lg:in-data-[sidebar-open=false]:start-[calc(var(--sidebar-collapsed-width))] transition-all duration-300 end-0 z-10 px-5 flex flex-wrap items-center justify-between gap-2.5 min-h-(--toolbar-height) bg-background border-b border-border backdrop-blur-sm supports-backdrop-filter:bg-background/60 shrink-0">
      {children}
    </div>
  );
}

function ToolbarActions({ children }: { children?: ReactNode }) {
  return <div className="flex items-center gap-2.5">{children}</div>;
}

function ToolbarBreadcrumbs({ portalType = 'platform' }: { portalType?: PortalType }) {
  const { pathname } = useLocation();
  const leaf = useBreadcrumbTrailLeaf();
  const trail = buildBreadcrumbTrail(pathname, portalType);
  // Structural section crumbs (e.g. "Inventory") carry no path of their own;
  // point them at the active module's dashboard instead of "#".
  const sectionHomePath = resolveModuleHomePath(pathname, portalType);

  // Menu-derived ancestors, plus an optional dynamic leaf (e.g. a record name
  // on a detail page) registered via useBreadcrumbLeaf.
  const crumbs: Array<{ title: string; path?: string }> = trail.map((item) => ({
    title: String(item.title),
    path: item.path ?? sectionHomePath,
  }));
  if (leaf) {
    crumbs.push({ title: leaf });
  }

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={HOME_PATH}>Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;

          return (
            <Fragment key={index}>
              <BreadcrumbSeparator className="text-xs text-muted-foreground">/</BreadcrumbSeparator>
              <BreadcrumbItem>
                {!isLast ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path || '#'}>{crumb.title}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ToolbarHeading ({ children }: { children: ReactNode }) {
  return <div className="flex flex-col md:flex-row md:items-center flex-wrap gap-1 lg:gap-5">{children}</div>;
}

function ToolbarPageTitle ({ children }: { children?: string }) {
  const { pathname } = useLocation();
  const { getCurrentItem } = useMenu(pathname);
  const item = getCurrentItem(MENU_SIDEBAR_MAIN);

  return (
    <h1 className="text-base font-medium leading-none text-foreground">
      {children ? children : item?.title || 'Untitled'}
    </h1>
  );
};

function ToolbarDescription ({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
      {children}
    </div>
  );
};

export {
  Toolbar,
  ToolbarActions,
  ToolbarBreadcrumbs,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription
};